import { useTranslation } from 'react-i18next';
import { Database, Link2 } from 'lucide-react';
import {
  eventLabel,
  formatActor,
  formatAuditDateTime,
  isDbChange,
  loginFailureReason,
  type AuditLogEvent,
  type AuditLogFilters,
} from '@entities/audit-log';
import { Button, Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle, useToast } from '@shared/ui';
import { AuditChangesView, AuditJsonView, AuditOutcomeBadge } from '@entities/audit-log';

interface AuditEventDetailProps {
  event: AuditLogEvent | null;
  open: boolean;
  projectName: (projectId: string) => string;
  onOpenChange: (open: boolean) => void;
  onQuickFilter: (patch: Partial<AuditLogFilters>) => void;
}

const Row = ({ label, value }: { label: string; value: string }) => (
  <div className="grid gap-1 sm:grid-cols-[9rem_1fr]">
    <dt className="text-xs text-muted-foreground">{label}</dt>
    <dd className="min-w-0 whitespace-pre-wrap break-all text-sm text-foreground">{value}</dd>
  </div>
);

export function AuditEventDetail({
  event,
  open,
  projectName,
  onOpenChange,
  onQuickFilter,
}: AuditEventDetailProps) {
  const { t } = useTranslation('cabinet');
  const { toast } = useToast();
  if (!event) return null;

  const actor = formatActor(event.actor, t);
  const failure = loginFailureReason(event, t);
  const copyLink = async () => {
    const url = new URL(window.location.href);
    url.searchParams.set('event', event.id);
    await navigator.clipboard.writeText(url.toString());
    toast(t('auditLog.copied'), 'success');
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader className="border-b border-border px-4 py-4 sm:px-6">
          <SheetTitle>{eventLabel(event.eventType, event.description, t)}</SheetTitle>
          <SheetDescription className="font-mono text-xs">{event.eventType}</SheetDescription>
        </SheetHeader>
        <SheetBody className="space-y-5">
          {isDbChange(event) ? (
            <div className="flex items-start gap-2 rounded-lg border border-border bg-pending px-3 py-2 text-sm text-pending-foreground">
              <Database className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{t('auditLog.dbChangeHint')}</span>
            </div>
          ) : null}

          <section className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <AuditOutcomeBadge outcome={event.outcome} />
              <span className="rounded-full bg-secondary px-2 py-0.5 text-xs">
                {t(`auditLog.categories.${event.category}`, { defaultValue: event.category })}
              </span>
            </div>
            <dl className="space-y-2">
              <Row label={t('auditLog.columns.time')} value={formatAuditDateTime(event.occurredAt)} />
              <Row label="UTC" value={event.occurredAt} />
              <Row label={t('auditLog.detail.id')} value={event.id} />
              {failure ? <Row label={t('auditLog.detail.loginReason')} value={failure} /> : null}
            </dl>
            <Button type="button" variant="outline" size="sm" onClick={() => void copyLink()}>
              <Link2 className="h-4 w-4" />
              {t('auditLog.detail.copyLink')}
            </Button>
          </section>

          <section className="space-y-2">
            <h3 className="text-sm font-medium">{t('auditLog.detail.who')}</h3>
            <dl className="space-y-2">
              <Row label={t('auditLog.columns.actor')} value={[actor.title, actor.detail].filter(Boolean).join(' · ')} />
              {event.actor.role ? <Row label={t('auditLog.detail.role')} value={event.actor.role} /> : null}
              {event.actor.label ? <Row label={t('auditLog.detail.label')} value={event.actor.label} /> : null}
              {event.actor.apiKeyFingerprint ? (
                <Row label={t('auditLog.detail.fingerprint')} value={event.actor.apiKeyFingerprint} />
              ) : null}
              {event.subjectUserId != null ? (
                <Row label={t('auditLog.detail.subject')} value={`#${event.subjectUserId}`} />
              ) : null}
            </dl>
          </section>

          <section className="space-y-2">
            <h3 className="text-sm font-medium">{t('auditLog.detail.what')}</h3>
            <dl className="space-y-2">
              <Row
                label={t('auditLog.columns.target')}
                value={event.target ? `${event.target.type}${event.target.id ? ` ${event.target.id}` : ''}` : '—'}
              />
              <Row
                label={t('auditLog.columns.project')}
                value={event.projectId ? projectName(event.projectId) : '—'}
              />
            </dl>
          </section>

          {event.reason ? (
            <section className="space-y-2">
              <h3 className="text-sm font-medium">{t('auditLog.detail.reason')}</h3>
              <p className="whitespace-pre-wrap break-all text-sm">{event.reason}</p>
            </section>
          ) : null}

          <AuditChangesView changes={event.changes} />

          <section className="space-y-2">
            <h3 className="text-sm font-medium">{t('auditLog.detail.context')}</h3>
            <dl className="space-y-2">
              <Row label={t('auditLog.columns.ip')} value={event.ip || '—'} />
              <Row label="User-Agent" value={event.userAgent || '—'} />
              <Row label="Request ID" value={event.requestId || '—'} />
              <Row label={t('auditLog.detail.session')} value={event.sessionRef || '—'} />
            </dl>
          </section>

          {event.metadata != null ? (
            <AuditJsonView title={t('auditLog.detail.metadata')} value={event.metadata} />
          ) : null}

          <p className="text-sm text-muted-foreground">
            {t('auditLog.detail.merchantVisible', {
              value: event.merchantVisible ? t('auditLog.yes') : t('auditLog.no'),
            })}
          </p>

          <div className="flex flex-wrap gap-2">
            {event.actor.userId != null ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onQuickFilter({ actorUserId: String(event.actor.userId) })}
              >
                {t('auditLog.quick.actor')}
              </Button>
            ) : null}
            {event.subjectUserId != null ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onQuickFilter({ subjectUserId: String(event.subjectUserId) })}
              >
                {t('auditLog.quick.subject')}
              </Button>
            ) : null}
            {event.target?.type && event.target.id ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  onQuickFilter({ targetType: event.target?.type ?? '', targetId: event.target?.id ?? '' })
                }
              >
                {t('auditLog.quick.target')}
              </Button>
            ) : null}
            {event.projectId ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onQuickFilter({ projectId: event.projectId ?? '' })}
              >
                {t('auditLog.quick.project')}
              </Button>
            ) : null}
            {event.requestId ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onQuickFilter({ requestId: event.requestId ?? '' })}
              >
                {t('auditLog.quick.request')}
              </Button>
            ) : null}
          </div>
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}
